/**
 * Acessibilidade (2026-10-16), no molde do techia hub: tamanho do texto,
 * fonte, espaço entre linhas, versão compacta e foco reforçado. Como o tema
 * e o modo de cor, mora no `localStorage` (é do navegador da pessoa) e vira
 * atributo no `<html>`, que `styles/accessibility.css` lê. O `index.html`
 * repete a aplicação antes da primeira pintura — mexeu aqui, mexe lá.
 */

export type TextScale = "small" | "regular" | "large" | "xlarge";
export type ReadingFont = "padrao" | "legivel" | "sistema";
export type LineSpacing = "padrao" | "amplo";
export type Density = "padrao" | "compacta";

export type AccessibilityPrefs = {
  textScale: TextScale;
  font: ReadingFont;
  lineSpacing: LineSpacing;
  density: Density;
  strongFocus: boolean;
};

export const ACCESSIBILITY_STORAGE_KEY = "reputation_accessibility";

export const DEFAULT_ACCESSIBILITY: AccessibilityPrefs = {
  textScale: "regular",
  font: "padrao",
  lineSpacing: "padrao",
  density: "padrao",
  strongFocus: false
};

const TEXT_SCALES: TextScale[] = ["small", "regular", "large", "xlarge"];
const FONTS: ReadingFont[] = ["padrao", "legivel", "sistema"];
const LINE_SPACINGS: LineSpacing[] = ["padrao", "amplo"];
const DENSITIES: Density[] = ["padrao", "compacta"];

function pick<T extends string>(value: unknown, allowed: T[], fallback: T): T {
  return typeof value === "string" && (allowed as string[]).includes(value) ? (value as T) : fallback;
}

export function readAccessibility(): AccessibilityPrefs {
  try {
    const raw = JSON.parse(localStorage.getItem(ACCESSIBILITY_STORAGE_KEY) ?? "null") as Record<string, unknown> | null;
    if (!raw || typeof raw !== "object") return DEFAULT_ACCESSIBILITY;
    return {
      textScale: pick(raw.textScale, TEXT_SCALES, DEFAULT_ACCESSIBILITY.textScale),
      font: pick(raw.font, FONTS, DEFAULT_ACCESSIBILITY.font),
      lineSpacing: pick(raw.lineSpacing, LINE_SPACINGS, DEFAULT_ACCESSIBILITY.lineSpacing),
      density: pick(raw.density, DENSITIES, DEFAULT_ACCESSIBILITY.density),
      strongFocus: raw.strongFocus === true
    };
  } catch {
    return DEFAULT_ACCESSIBILITY;
  }
}

export function saveAccessibility(prefs: AccessibilityPrefs): void {
  try {
    localStorage.setItem(ACCESSIBILITY_STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // Sem storage (aba privada): vale só nesta sessão.
  }
  applyAccessibility(prefs);
}

const LEGIBLE_FONT_LINK_ID = "reputation-font-atkinson";

/** A fonte de leitura fácil (Atkinson Hyperlegible) só é baixada por quem a
 *  escolhe. */
function ensureLegibleFont(): void {
  if (document.getElementById(LEGIBLE_FONT_LINK_ID)) return;
  const link = document.createElement("link");
  link.id = LEGIBLE_FONT_LINK_ID;
  link.rel = "stylesheet";
  link.href = "https://fonts.googleapis.com/css2?family=Atkinson+Hyperlegible:ital,wght@0,400;0,700;1,400&display=swap";
  document.head.appendChild(link);
}

function setAttr(root: HTMLElement, name: string, value: string | null): void {
  if (value === null) root.removeAttribute(name);
  else root.setAttribute(name, value);
}

export function applyAccessibility(prefs: AccessibilityPrefs = readAccessibility()): void {
  const root = document.documentElement;
  setAttr(root, "data-text-scale", prefs.textScale === "regular" ? null : prefs.textScale);
  setAttr(root, "data-font", prefs.font === "padrao" ? null : prefs.font);
  setAttr(root, "data-line-spacing", prefs.lineSpacing === "padrao" ? null : prefs.lineSpacing);
  setAttr(root, "data-density", prefs.density === "padrao" ? null : prefs.density);
  setAttr(root, "data-focus", prefs.strongFocus ? "strong" : null);
  if (prefs.font === "legivel") ensureLegibleFont();
}
